import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import "../AboutUs/AboutUs.css";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import { getPersonByIdOrLegacyId } from "../../Services/Person";
import { getFirmById } from "../../Services/Firm";
import { IPerson } from "../../interfaces/models/IPerson";
import type { IPersonView } from "../../interfaces/models/IPersonView";
import AuthContext from "../../store/AuthContext";
import { PersonFormProvider } from "../../components/Organismos/Person/personForm/PersonFormContext";
import EditMyProfileForm from "./editMyProfile/EditMyProfileForm";

function breadcrumbLabelsForPathPrefixes(
  pathname: string,
  label: string
): Record<string, string> {
  const segments = pathname.split("/").filter(Boolean);
  const labels: Record<string, string> = {};
  let acc = "";
  for (const seg of segments) {
    acc += `/${seg}`;
    labels[acc.toLowerCase()] = label;
  }
  return labels;
}

/**
 * Member profile editor: same fields and save pipeline as Manage Firm → Professionals → Create
 * (`PersonFormProvider` + Organismos sections), one “Update profile” action at the end.
 */
const EditMyProfilePage: React.FC = () => {
  const { generatedId } = useParams<{
    customPath: string;
    generatedId: string;
  }>();
  const location = useLocation();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const { loggedPerson } = useContext(AuthContext);

  const [person, setPerson] = useState<IPerson | null>(null);
  const [listingCategory, setListingCategory] = useState<string[] | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!generatedId) {
      setLoading(false);
      setError("Missing profile id");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const p = await getPersonByIdOrLegacyId(generatedId);
        if (cancelled) {
          return;
        }
        if (!p) {
          setPerson(null);
          setError("Profile not found.");
          return;
        }
        setPerson(p);
      } catch {
        if (!cancelled) {
          setError("Could not load profile.");
          setPerson(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [generatedId]);

  useEffect(() => {
    if (!person?.firm?.trim()) {
      setListingCategory(undefined);
      return;
    }
    let cancelled = false;
    void getFirmById(person.firm.trim())
      .then((firm) => {
        if (cancelled) {
          return;
        }
        const cats = firm?.listingCategory;
        setListingCategory(
          Array.isArray(cats) && cats.length ? [...cats] : undefined
        );
      })
      .catch(() => {
        if (!cancelled) {
          setListingCategory(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [person?.firm]);

  useEffect(() => {
    const title = "Edit My Profile";
    setPageMeta({
      title,
      breadcrumbLabels: breadcrumbLabelsForPathPrefixes(location.pathname, title),
      titleAction: null,
    });
    return () => clearPageMeta();
  }, [location.pathname, setPageMeta, clearPageMeta]);

  const isOwnProfile = useMemo(
    () =>
      !!loggedPerson?.userId &&
      !!person?.userId &&
      loggedPerson.userId === person.userId,
    [loggedPerson?.userId, person?.userId]
  );

  const initialPersonView = useMemo(
    () => (person ? (person as unknown as IPersonView) : null),
    [person]
  );

  const canEditForm = useMemo(
    () =>
      Boolean(
        isOwnProfile &&
          person?.id?.trim() &&
          initialPersonView?.id &&
          !loading &&
          !error
      ),
    [isOwnProfile, person?.id, initialPersonView?.id, loading, error]
  );

  if (loading) {
    return (
      <div className="w-full px-4 py-12 text-center text-gray-font">
        Loading profile…
      </div>
    );
  }

  if (error || !person || !initialPersonView) {
    return (
      <div className="w-full px-4 py-12 text-center text-gray-font">
        {error ?? "Profile not found."}
      </div>
    );
  }

  if (!isOwnProfile) {
    return (
      <div className="w-full px-4 py-12 text-center text-gray-font">
        You can only edit your own profile.
      </div>
    );
  }

  if (!canEditForm) {
    return (
      <div className="w-full px-4 py-12 text-center text-gray-font">
        Unable to open the editor for this profile.
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-hidden">
      <PersonFormProvider
        mode="edit"
        firmSource="selectable"
        initialPerson={initialPersonView}
      >
        {generatedId ? (
          <EditMyProfileForm
            generatedId={generatedId}
            listingCategory={listingCategory}
          />
        ) : null}
      </PersonFormProvider>
    </div>
  );
};

export default EditMyProfilePage;
